// Reduced motion. The page calms down when your system asks for it or when the motion switch
// is off. The system setting wins, same as on the old site: the switch is a saved choice, and
// it keeps its position while the system reduces motion. The head script puts the saved
// choice on `<html>` as `data-motion`, so CSS can read it too (the `reduced-motion` mixin in
// _tokens.scss).

/** The motion switch's saved choice. The head script sets `data-motion` from it. */
export type MotionChoice = "on" | "off";

interface MotionState {
  /** The switch is on. */
  switchOn: boolean;
  /** The page reduces motion. */
  reduced: boolean;
}

/** The switch's position and the page's motion, from the saved choice (if any) and the
    system setting. With no saved choice, the switch follows the system setting. */
export function motionState(saved: string | undefined, systemReduces: boolean): MotionState {
  const switchOn = saved === "on" || (saved !== "off" && !systemReduces);
  return { switchOn, reduced: systemReduces || !switchOn };
}

// One query for the whole page, so every listener hears every change.
let query: MediaQueryList | undefined;
const systemQuery = (): MediaQueryList =>
  (query ??= matchMedia("(prefers-reduced-motion: reduce)"));

export function systemReducesMotion(): boolean {
  return systemQuery().matches;
}

export function currentMotion(): MotionState {
  return motionState(document.documentElement.dataset.motion, systemReducesMotion());
}

export function motionReduced(): boolean {
  return currentMotion().reduced;
}

/** Calls `listener` after the system setting or the switch changes. */
export function onMotionChange(listener: () => void, options: { signal?: AbortSignal } = {}): void {
  systemQuery().addEventListener("change", listener, options);
  document.addEventListener("jrsm-motion", listener, options);
}
